import { ref, computed } from 'vue'
import { invoke } from '@tauri-apps/api/core'

const deviceUuid = ref(null)
const deviceType = ref(null)
const registrationStatus = ref('unknown') // unknown, pending, approved, disabled
const assignedTerminalId = ref(null)
const assignedTerminal = ref(null)
const isPolling = ref(false)
const pollInterval = ref(null)

const POLL_INTERVAL_MS = 5000
const UUID_STORAGE_KEY = 'device_uuid'

export function useDeviceRegistration() {
  const { getPosSetting, savePosSetting } = usePosDatabase()
  const userStore = useUserStore()

  const isPendingApproval = computed(() => registrationStatus.value === 'pending')
  const isApproved = computed(() => registrationStatus.value === 'approved')
  const isDisabled = computed(() => registrationStatus.value === 'disabled')

  async function getOrCreateDeviceUuid() {
    if (deviceUuid.value) return deviceUuid.value

    let uuid = await getPosSetting(UUID_STORAGE_KEY)

    if (!uuid) {
      uuid = generateUuid()
      await savePosSetting(UUID_STORAGE_KEY, uuid)
      console.log('[DeviceRegistration] Generated new device UUID:', uuid)
    } else {
      console.log('[DeviceRegistration] Loaded existing device UUID:', uuid)
    }

    deviceUuid.value = uuid
    return uuid
  }

  function generateUuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  async function getDeviceType() {
    if (deviceType.value) return deviceType.value

    try {
      const type = await invoke('get_device_type')
      deviceType.value = type || 'pos'
    } catch {
      deviceType.value = 'pos'
    }

    return deviceType.value
  }

  async function getPlatform() {
    try {
      const platform = await invoke('get_platform')
      return platform || 'windows'
    } catch {
      return 'web'
    }
  }

  async function getAppVersion() {
    try {
      const version = await invoke('get_app_version')
      return version || '1.0.0'
    } catch {
      return '1.0.0'
    }
  }

  async function registerDevice() {
    const uuid = await getOrCreateDeviceUuid()
    const type = await getDeviceType()
    const platform = await getPlatform()
    const appVersion = await getAppVersion()
    const businessId = userStore.activeBusinessId

    if (!businessId) {
      console.error('[DeviceRegistration] No business ID available')
      return { success: false, error: 'No business selected' }
    }

    try {
      console.log('[DeviceRegistration] Registering device:', { uuid, type, platform, businessId })

      const response = await useApi('/api/devices/register', {
        deviceUuid: uuid,
        deviceType: type,
        platform,
        appVersion,
        businessId,
      }, { method: 'POST' }, 2)

      if (response.device) {
        registrationStatus.value = response.device.isApproved ? 'approved' :
                                   response.device.isEnabled ? 'pending' : 'disabled'
        assignedTerminalId.value = response.device.assignedTerminalId

        console.log('[DeviceRegistration] Registration response:', {
          status: registrationStatus.value,
          isNewRegistration: response.isNewRegistration,
          assignedTerminalId: assignedTerminalId.value,
        })

        if (registrationStatus.value === 'approved') {
          await savePosSetting('assignedTerminalId', assignedTerminalId.value)
          if (response.device.assignedTerminal) {
            const terminal = response.device.assignedTerminal
            await savePosSetting('paymentProvider', terminal.provider)
            await savePosSetting('terminalConfig', JSON.stringify({
              provider: terminal.provider,
              terminalId: terminal.terminalId,
              merchantId: terminal.merchantId,
              apiKey: terminal.apiKey,
              integrationKey: terminal.integrationKey,
              isSandbox: terminal.isSandbox,
              stationId: terminal.stationId,
              additionalConfig: terminal.additionalConfig,
            }))
            console.log('[DeviceRegistration] Saved terminal config for provider:', terminal.provider)
          }
        }

        return {
          success: true,
          status: registrationStatus.value,
          isNewRegistration: response.isNewRegistration,
          device: response.device,
        }
      }

      return { success: false, error: 'Invalid response' }
    } catch (error) {
      console.error('[DeviceRegistration] Registration failed:', error)
      return { success: false, error: error.message || 'Registration failed' }
    }
  }

  async function checkStatus() {
    const uuid = deviceUuid.value
    if (!uuid) return null

    try {
      const response = await useApi(`/api/devices/${uuid}/status`, {}, { method: 'GET' }, 2)

      if (!response.isEnabled) {
        registrationStatus.value = 'disabled'
      } else if (response.isApproved) {
        registrationStatus.value = 'approved'
      } else {
        registrationStatus.value = 'pending'
      }

      assignedTerminalId.value = response.assignedTerminalId
      assignedTerminal.value = response.assignedTerminal

      if (registrationStatus.value === 'approved' && assignedTerminalId.value) {
        await savePosSetting('assignedTerminalId', assignedTerminalId.value)
        if (assignedTerminal.value) {
          const terminal = assignedTerminal.value
          await savePosSetting('paymentProvider', terminal.provider)
          await savePosSetting('terminalConfig', JSON.stringify({
            provider: terminal.provider,
            terminalId: terminal.terminalId,
            merchantId: terminal.merchantId,
            apiKey: terminal.apiKey,
            integrationKey: terminal.integrationKey,
            isSandbox: terminal.isSandbox,
            stationId: terminal.stationId,
            additionalConfig: terminal.additionalConfig,
          }))
          console.log('[DeviceRegistration] Saved terminal config from status check:', terminal.provider)
        }
      }

      return {
        status: registrationStatus.value,
        assignedTerminalId: assignedTerminalId.value,
        assignedTerminal: assignedTerminal.value,
      }
    } catch (error) {
      console.error('[DeviceRegistration] Status check failed:', error)
      return null
    }
  }

  function startPollingForApproval(onApproved) {
    if (isPolling.value) return

    isPolling.value = true
    console.log('[DeviceRegistration] Starting approval polling...')

    pollInterval.value = setInterval(async () => {
      const result = await checkStatus()

      if (result?.status === 'approved') {
        stopPolling()
        console.log('[DeviceRegistration] Device approved!')
        if (onApproved) onApproved(result)
      } else if (result?.status === 'disabled') {
        stopPolling()
        console.log('[DeviceRegistration] Device disabled!')
      }
    }, POLL_INTERVAL_MS)
  }

  function stopPolling() {
    if (pollInterval.value) {
      clearInterval(pollInterval.value)
      pollInterval.value = null
    }
    isPolling.value = false
    console.log('[DeviceRegistration] Stopped polling')
  }

  async function getAssignedTerminalId() {
    if (assignedTerminalId.value) return assignedTerminalId.value

    const stored = await getPosSetting('assignedTerminalId')
    if (stored) {
      assignedTerminalId.value = stored
      return stored
    }

    await checkStatus()
    return assignedTerminalId.value
  }

  function reset() {
    stopPolling()
    registrationStatus.value = 'unknown'
    assignedTerminalId.value = null
    assignedTerminal.value = null
  }

  return {
    deviceUuid,
    deviceType,
    registrationStatus,
    assignedTerminalId,
    assignedTerminal,
    isPendingApproval,
    isApproved,
    isDisabled,
    isPolling,
    getOrCreateDeviceUuid,
    getDeviceType,
    registerDevice,
    checkStatus,
    startPollingForApproval,
    stopPolling,
    getAssignedTerminalId,
    reset,
  }
}
